<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

// Single-line text that truncates with an ellipsis when it overflows, revealing the full
// value on hover via the shared PrimeVue tooltip — but ONLY when the text is actually
// clipped (a tooltip on text that already fits is just noise). The caller controls the
// width by passing layout classes (e.g. `class="w-1/2 min-w-0"`) — they merge onto the root.
const props = withDefaults(
  defineProps<{
    text?: string | null;
    // Shown (and tooltipped) when `text` is empty.
    fallback?: string;
  }>(),
  { fallback: '—' },
);

const el = ref<HTMLElement | null>(null);
const isOverflowing = ref(false);

const measure = () => {
  const node = el.value;
  // +1 guards against sub-pixel rounding reporting a false overflow.
  isOverflowing.value = !!node && node.scrollWidth > node.clientWidth + 1;
};

let observer: ResizeObserver | null = null;
onMounted(() => {
  measure();
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure);
    if (el.value) observer.observe(el.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());
watch(() => props.text, () => nextTick(measure));
</script>

<template>
  <span
    ref="el"
    v-tooltip.top="{ value: text || fallback, disabled: !isOverflowing, showDelay: 150 }"
    class="block truncate"
  >{{ text || fallback }}</span>
</template>
